import React, { useEffect, useState } from 'react';
import Navbar from '../../shared/navbar.js';
import { API_BASE_URL } from '../../config';
import './ApplicantDashboard.css';
import { useParams, useNavigate } from 'react-router-dom';
import ApplicationMilestones from './ApplicationMilestones';
import CommunicationsLog from '../crm/CommunicationsLog';
import TasksList from '../dashboard/TasksList';
import CommentsThread from '../crm/CommentsThread';

const STATUS_OPTIONS = [
  { value: 'new',         label: 'New Inquiry',     color: '#3b82f6' },
  { value: 'contacted',   label: 'Contacted',       color: '#06b6d4' },
  { value: 'qualified',   label: 'Qualified',       color: '#10b981' },
  { value: 'viewing',     label: 'Viewing',         color: '#f59e0b' },
  { value: 'application', label: 'Application',     color: '#8b5cf6' },
  { value: 'background',  label: 'Background Check',color: '#6366f1' },
  { value: 'screening',   label: 'Screening',       color: '#a855f7' },
  { value: 'approved',    label: 'Approved',        color: '#059669' },
  { value: 'lease_signed',label: 'Lease Signed',    color: '#0d9488' },
  { value: 'rejected',    label: 'Rejected',        color: '#ef4444' },
  { value: 'moved_in',    label: 'Moved In',        color: '#047857' },
];

const DOCUMENT_CATEGORIES = [
  'ID/Personal Info', 'Credit & Finances', 'Employment', 'References', 'Lease Documents', 'Other',
];

function getStatusInfo(status) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

function getInitials(first, last) {
  return `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase() || 'A';
}

export default function ApplicantDashboard() {
  const { applicantId } = useParams();
  const navigate = useNavigate();

  // ─── State ───────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('activity');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [applicant, setApplicant] = useState(null);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sidebarEditOpen, setSidebarEditOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [properties, setProperties] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [uploadDescription, setUploadDescription] = useState('');
  
  // Conversion/Approval state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [approvalData, setApprovalData] = useState({
    property_id: '',
    unit_id: '',
    monthly_rent: '',
    move_in_date: '',
    lease_type: '12 months'
  });
  const [converting, setConverting] = useState(false);
  const [units, setUnits] = useState([]);

  // ─── Data Loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [appRes, propRes, notesRes, docsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/applicants/${applicantId}`),
          fetch(`${API_BASE_URL}/api/properties`),
          fetch(`${API_BASE_URL}/api/applicants/${applicantId}/notes`),
          fetch(`${API_BASE_URL}/api/applicants/${applicantId}/documents`),
        ]);
        if (!appRes.ok) throw new Error('Failed to load applicant');
        const data = await appRes.json();
        setApplicant(data);
        setFormData({ ...data });

        if (propRes.ok) {
          const pData = await propRes.json();
          setProperties(Array.isArray(pData) ? pData.map((p) => ({ id: p.id, name: p.property_name || `Property ${p.id}` })) : []);
        }
        if (notesRes.ok) { const nData = await notesRes.json(); setNotes(nData.notes || []); }
        if (docsRes.ok) { setDocuments(await docsRes.json() || []); }
      } catch (e) {
        setError(e.message || 'Error loading applicant');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [applicantId]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const handleSave = async () => {
    if (!formData) return;
    try {
      setSaving(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
      setSuccess('Saved');
      setApplicant({ ...formData });
      setSidebarEditOpen(false);
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    const text = (newNote || '').trim();
    if (!text) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_text: text, created_by: localStorage.getItem('username') || '' }),
      });
      if (res.ok) { const d = await res.json(); setNotes((p) => [d.note, ...p]); setNewNote(''); }
    } catch (e) { setError(e.message); }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('category', uploadCategory);
      fd.append('description', uploadDescription);
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/documents`, { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Upload failed'); }
      const docsRes = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/documents`);
      if (docsRes.ok) setDocuments(await docsRes.json() || []);
      setUploadFile(null); setUploadDescription(''); setUploadCategory('Other');
      setSuccess('Document uploaded');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/documents/${docId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setDocuments((d) => d.filter((x) => x.id !== docId));
    } catch (e) { setError(e.message); }
  };

  const handleDownloadDocument = (docId) => {
    window.open(`${API_BASE_URL}/api/applicants/${applicantId}/documents/${docId}/download`, '_blank');
  };

  // Fetch units when property changes
  const fetchUnits = async (propertyId) => {
    if (!propertyId) {
      setUnits([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/units`);
      if (res.ok) {
        const data = await res.json();
        setUnits(data.filter(u => u.status === 'vacant'));
      }
    } catch (e) {
      console.error('Failed to fetch units:', e);
    }
  };

  // Handle approval for lease
  const handleApproveForLease = async () => {
    if (!approvalData.property_id || !approvalData.monthly_rent) {
      setError('Property and monthly rent are required for approval');
      return;
    }
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/approve-for-lease`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(approvalData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Approval failed');
      
      setSuccess('Applicant approved! Email sent with lease details.');
      setShowApprovalModal(false);
      // Refresh applicant data
      const appRes = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}`);
      if (appRes.ok) {
        const appData = await appRes.json();
        setApplicant(appData);
        setFormData({ ...appData });
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle conversion to tenant
  const handleConvertToTenant = async () => {
    if (!window.confirm('Are you sure you want to convert this applicant to a tenant? This will create a new tenant account and generate their rent schedule.')) {
      return;
    }
    try {
      setConverting(true);
      const conversionData = {
        property_id: approvalData.property_id || formData?.assigned_property_id,
        unit_id: approvalData.unit_id || formData?.assigned_unit_id,
        monthly_rent: approvalData.monthly_rent || formData?.approved_rent,
        lease_start_date: approvalData.move_in_date || formData?.move_in_date,
        lease_type: approvalData.lease_type || formData?.lease_length || '12 months'
      };
      
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/convert-to-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(conversionData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Conversion failed');
      
      setSuccess(`Successfully converted to tenant! Tenant ID: ${data.tenant_id}, Code: ${data.tenant_code}`);
      setShowConversionModal(false);
      
      // Navigate to tenant page after short delay
      setTimeout(() => {
        navigate(`/tenant/${data.tenant_id}`);
      }, 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setConverting(false);
    }
  };

  // ─── Utilities ───────────────────────────────────────────────────────────────
  const formatDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; } };
  const formatCurrency = (n) => { if (!n) return '—'; return `$${parseFloat(n).toLocaleString()}`; };
  const formatFileSize = (b) => { if (!b) return '—'; if (b < 1024) return `${b} B`; if (b < 1048576) return `${(b/1024).toFixed(1)} KB`; return `${(b/1048576).toFixed(1)} MB`; };
  const getDocsByCategory = (cat) => documents.filter((d) => d.category === cat);

  const applicantName = formData ? `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Applicant' : 'Applicant';
  const statusInfo = getStatusInfo(formData?.status);

  const TABS = [
    { key: 'activity',   label: 'Activity',    icon: '📋' },
    { key: 'tasks',      label: 'Tasks',       icon: '✅' },
    { key: 'details',    label: 'Details',     icon: '📝' },
    { key: 'documents',  label: 'Documents',   icon: '📁' },
    { key: 'references', label: 'References',  icon: '👥' },
    { key: 'milestones', label: 'Milestones',  icon: '🗓' },
  ];

  return (
    <div className="apd-page">
      <Navbar selected="Databases" />

      {loading && (
        <div className="apd-loading">
          <div className="spinner" />
          <p>Loading applicant…</p>
        </div>
      )}

      {!loading && formData && (
        <>
          {/* ─── Top Header Bar ──────────────────────────────────────────────── */}
          <div className="apd-topbar">
            <button className="apd-back-btn" onClick={() => navigate(-1)}>
              ← Back
            </button>

            <div className="apd-topbar-identity">
              <div className="apd-avatar">{getInitials(formData.first_name, formData.last_name)}</div>
              <div>
                <h1 className="apd-name">{applicantName}</h1>
                {formData.email && <span className="apd-email">{formData.email}</span>}
              </div>
            </div>

            <div className="apd-topbar-controls">
              <select
                className="apd-status-select"
                value={formData.status || 'new'}
                style={{ '--status-color': statusInfo.color }}
                onChange={(e) => {
                  handleChange('status', e.target.value);
                  // auto-save status change
                  fetch(`${API_BASE_URL}/api/applicants/${applicantId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: e.target.value }),
                  });
                }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              {formData.phone && (
                <a href={`tel:${formData.phone}`} className="apd-quick-btn apd-call-btn" title="Call">
                  📞 Call
                </a>
              )}
              {formData.email && (
                <a href={`mailto:${formData.email}`} className="apd-quick-btn apd-email-btn" title="Email">
                  📧 Email
                </a>
              )}
              <button
                className="apd-save-btn"
                onClick={handleSave}
                disabled={saving || !formData}
              >
                {saving ? 'Saving…' : '✓ Save'}
              </button>
              
              {/* Approval Button - shows when not yet approved */}
              {formData && !['approved', 'lease_signed', 'moved_in', 'rejected'].includes(formData.status) && (
                <button
                  className="apd-approve-btn"
                  onClick={() => setShowApprovalModal(true)}
                  style={{ backgroundColor: '#059669', color: 'white', marginLeft: '8px' }}
                >
                  ✓ Approve for Lease
                </button>
              )}
              
              {/* Convert Button - shows when approved or lease signed */}
              {formData && ['approved', 'lease_signed'].includes(formData.status) && !formData.converted_to_tenant_id && (
                <button
                  className="apd-convert-btn"
                  onClick={() => setShowConversionModal(true)}
                  style={{ backgroundColor: '#7c3aed', color: 'white', marginLeft: '8px' }}
                  disabled={converting}
                >
                  {converting ? 'Converting…' : '🔄 Convert to Tenant'}
                </button>
              )}
              
              {/* Already converted indicator */}
              {formData?.converted_to_tenant_id && (
                <button
                  className="apd-converted-btn"
                  onClick={() => navigate(`/tenant/${formData.converted_to_tenant_id}`)}
                  style={{ backgroundColor: '#047857', color: 'white', marginLeft: '8px' }}
                >
                  👤 View Tenant
                </button>
              )}
            </div>
          </div>

          {error && <div className="apd-alert apd-alert-error">{error}</div>}
          {success && <div className="apd-alert apd-alert-success">{success}</div>}

          {/* ─── Two-Column Layout ───────────────────────────────────────────── */}
          <div className="apd-body">
            {/* Left Sidebar */}
            <aside className="apd-sidebar">
              {/* Score chip */}
              <div className="apd-sidebar-score">
                <span className="apd-score-label">Lead Score</span>
                <div
                  className="apd-score-ring"
                  style={{ '--score': formData.lead_score ?? 85 }}
                >
                  <span className="apd-score-value">{formData.lead_score ?? 85}</span>
                </div>
              </div>

              {/* Key Facts */}
              <div className="apd-sidebar-section">
                <div className="apd-sidebar-section-header">
                  <span className="apd-sidebar-section-title">Key Details</span>
                  <button
                    className="apd-sidebar-edit-btn"
                    onClick={() => setSidebarEditOpen((p) => !p)}
                  >
                    {sidebarEditOpen ? 'Done' : 'Edit'}
                  </button>
                </div>

                {sidebarEditOpen ? (
                  <div className="apd-sidebar-edit-fields">
                    {[
                      { label: 'First Name', key: 'first_name', type: 'text' },
                      { label: 'Last Name',  key: 'last_name',  type: 'text' },
                      { label: 'Email',      key: 'email',      type: 'email' },
                      { label: 'Phone',      key: 'phone',      type: 'tel' },
                    ].map(({ label, key, type }) => (
                      <div key={key} className="apd-sf-row">
                        <label>{label}</label>
                        <input
                          type={type}
                          value={formData[key] || ''}
                          onChange={(e) => handleChange(key, e.target.value)}
                        />
                      </div>
                    ))}
                    <div className="apd-sf-row">
                      <label>Status</label>
                      <select value={formData.status || 'new'} onChange={(e) => handleChange('status', e.target.value)}>
                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div className="apd-sf-row">
                      <label>Lead Score</label>
                      <input type="number" min="0" max="100" value={formData.lead_score ?? 85}
                        onChange={(e) => handleChange('lead_score', parseInt(e.target.value) || 85)} />
                    </div>
                    <div className="apd-sf-row">
                      <label>Assigned Agent</label>
                      <input type="text" value={formData.assigned_agent || ''}
                        onChange={(e) => handleChange('assigned_agent', e.target.value)} />
                    </div>
                  </div>
                ) : (
                  <div className="apd-sidebar-facts">
                    <div className="apd-fact"><span className="apdf-label">Phone</span><span className="apdf-value">{formData.phone || '—'}</span></div>
                    <div className="apd-fact"><span className="apdf-label">Source</span><span className="apdf-value">{formData.source || '—'}</span></div>
                    <div className="apd-fact"><span className="apdf-label">Agent</span><span className="apdf-value">{formData.assigned_agent || 'Unassigned'}</span></div>
                    <div className="apd-fact"><span className="apdf-label">Created</span><span className="apdf-value">{formatDate(formData.created_at)}</span></div>
                    <div className="apd-fact"><span className="apdf-label">Last Contact</span><span className="apdf-value">{formatDate(formData.last_contact)}</span></div>
                  </div>
                )}
              </div>

              {/* Property Interest */}
              <div className="apd-sidebar-section">
                <div className="apd-sidebar-section-title">Property Interest</div>
                <div className="apd-sidebar-facts">
                  <div className="apd-fact"><span className="apdf-label">Property</span><span className="apdf-value">{formData.property_interest || '—'}</span></div>
                  <div className="apd-fact"><span className="apdf-label">Move-in</span><span className="apdf-value">{formatDate(formData.move_in_date)}</span></div>
                  <div className="apd-fact"><span className="apdf-label">Budget</span>
                    <span className="apdf-value">
                      {formData.budget_min || formData.budget_max
                        ? `${formatCurrency(formData.budget_min)} – ${formatCurrency(formData.budget_max)}`
                        : '—'}
                    </span>
                  </div>
                  <div className="apd-fact"><span className="apdf-label">Lease</span><span className="apdf-value">{formData.lease_length || '—'}</span></div>
                </div>
              </div>

              {/* Financials */}
              <div className="apd-sidebar-section">
                <div className="apd-sidebar-section-title">Financials</div>
                <div className="apd-sidebar-facts">
                  <div className="apd-fact"><span className="apdf-label">Income/mo</span><span className="apdf-value">{formatCurrency(formData.monthly_income)}</span></div>
                  <div className="apd-fact"><span className="apdf-label">Credit Score</span><span className="apdf-value">{formData.credit_score || '—'}</span></div>
                  <div className="apd-fact"><span className="apdf-label">Savings</span><span className="apdf-value">{formatCurrency(formData.savings)}</span></div>
                </div>
              </div>
            </aside>

            {/* Right Main Area */}
            <div className="apd-main">
              {/* Tab Bar */}
              <div className="apd-tabs">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`apd-tab ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span className="apd-tab-icon">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="apd-tab-body">
                {/* ── Activity ─────────────────────────────────────────── */}
                {activeTab === 'activity' && (
                  <div className="apd-tab-content">
                    <CommunicationsLog
                      applicantId={applicantId}
                      applicantName={applicantName}
                    />
                    <CommentsThread applicantId={applicantId} />
                  </div>
                )}

                {/* ── Tasks ────────────────────────────────────────────── */}
                {activeTab === 'tasks' && (
                  <div className="apd-tab-content">
                    <TasksList applicantId={applicantId} />
                  </div>
                )}

                {/* ── Details (full edit form) ──────────────────────────── */}
                {activeTab === 'details' && (
                  <div className="apd-tab-content">
                    <div className="apd-details-grid">
                      {/* Basic Info */}
                      <div className="apd-detail-section">
                        <h3>Basic Information</h3>
                        <div className="apd-field-grid">
                          {[
                            { label: 'First Name', key: 'first_name', type: 'text' },
                            { label: 'Last Name',  key: 'last_name',  type: 'text' },
                            { label: 'Email',      key: 'email',      type: 'email' },
                            { label: 'Phone',      key: 'phone',      type: 'tel' },
                          ].map(({ label, key, type }) => (
                            <div key={key} className="apd-field">
                              <label>{label}</label>
                              <input type={type} className="apd-input" value={formData[key] || ''}
                                onChange={(e) => handleChange(key, e.target.value)} />
                            </div>
                          ))}
                          <div className="apd-field">
                            <label>Source</label>
                            <select className="apd-input" value={formData.source || 'Website'}
                              onChange={(e) => handleChange('source', e.target.value)}>
                              {['Website','Referral','Walk-in','Advertisement','Other'].map((s) => (
                                <option key={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                          <div className="apd-field">
                            <label>Assigned Agent</label>
                            <input type="text" className="apd-input" value={formData.assigned_agent || ''}
                              onChange={(e) => handleChange('assigned_agent', e.target.value)} />
                          </div>
                          <div className="apd-field">
                            <label>Lead Score (0-100)</label>
                            <input type="number" className="apd-input" min="0" max="100"
                              value={formData.lead_score ?? 85}
                              onChange={(e) => handleChange('lead_score', parseInt(e.target.value) || 85)} />
                          </div>
                        </div>
                      </div>

                      {/* Property & Lease */}
                      <div className="apd-detail-section">
                        <h3>Property & Lease</h3>
                        <div className="apd-field-grid">
                          <div className="apd-field">
                            <label>Property Interest</label>
                            <input type="text" className="apd-input" value={formData.property_interest || ''}
                              onChange={(e) => handleChange('property_interest', e.target.value)}
                              placeholder="Property name or address" />
                          </div>
                          <div className="apd-field">
                            <label>Budget Min ($/mo)</label>
                            <input type="number" className="apd-input" value={formData.budget_min || ''}
                              onChange={(e) => handleChange('budget_min', e.target.value ? parseFloat(e.target.value) : null)} />
                          </div>
                          <div className="apd-field">
                            <label>Budget Max ($/mo)</label>
                            <input type="number" className="apd-input" value={formData.budget_max || ''}
                              onChange={(e) => handleChange('budget_max', e.target.value ? parseFloat(e.target.value) : null)} />
                          </div>
                          <div className="apd-field">
                            <label>Desired Move-in Date</label>
                            <input type="date" className="apd-input" value={formData.move_in_date || ''}
                              onChange={(e) => handleChange('move_in_date', e.target.value)} />
                          </div>
                          <div className="apd-field">
                            <label>Lease Length</label>
                            <select className="apd-input" value={formData.lease_length || ''}
                              onChange={(e) => handleChange('lease_length', e.target.value)}>
                              <option value="">Select…</option>
                              {['3 months','6 months','12 months','18 months','24 months','Month-to-month'].map((l) => (
                                <option key={l}>{l}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Financial */}
                      <div className="apd-detail-section">
                        <h3>Financial Information</h3>
                        <div className="apd-field-grid">
                          <div className="apd-field">
                            <label>Monthly Income ($)</label>
                            <input type="number" className="apd-input" value={formData.monthly_income || ''}
                              onChange={(e) => handleChange('monthly_income', e.target.value ? parseFloat(e.target.value) : null)} />
                          </div>
                          <div className="apd-field">
                            <label>Credit Score</label>
                            <input type="number" className="apd-input" min="300" max="850" value={formData.credit_score || ''}
                              onChange={(e) => handleChange('credit_score', e.target.value ? parseInt(e.target.value) : null)} />
                          </div>
                          <div className="apd-field">
                            <label>Savings ($)</label>
                            <input type="number" className="apd-input" value={formData.savings || ''}
                              onChange={(e) => handleChange('savings', e.target.value ? parseFloat(e.target.value) : null)} />
                          </div>
                          <div className="apd-field">
                            <label>Debts ($)</label>
                            <input type="number" className="apd-input" value={formData.debts || ''}
                              onChange={(e) => handleChange('debts', e.target.value ? parseFloat(e.target.value) : null)} />
                          </div>
                        </div>
                      </div>

                      {/* Employment */}
                      <div className="apd-detail-section">
                        <h3>Employment</h3>
                        <div className="apd-field-grid">
                          <div className="apd-field">
                            <label>Employer Name</label>
                            <input type="text" className="apd-input" value={formData.employer_name || ''}
                              onChange={(e) => handleChange('employer_name', e.target.value)} />
                          </div>
                          <div className="apd-field">
                            <label>Job Title</label>
                            <input type="text" className="apd-input" value={formData.job_title || ''}
                              onChange={(e) => handleChange('job_title', e.target.value)} />
                          </div>
                          <div className="apd-field">
                            <label>Employment Status</label>
                            <select className="apd-input" value={formData.employment_status || ''}
                              onChange={(e) => handleChange('employment_status', e.target.value)}>
                              <option value="">Select…</option>
                              {['Full-time','Part-time','Contract','Self-employed','Unemployed','Student','Retired'].map((s) => (
                                <option key={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Application Notes */}
                      <div className="apd-detail-section full-width">
                        <h3>Application Notes</h3>
                        <div className="apd-notes-compose">
                          <textarea
                            className="apd-input"
                            rows={3}
                            placeholder="Add an internal note…"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                          />
                          <button
                            className="apd-btn-primary"
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                          >
                            Add Note
                          </button>
                        </div>
                        <div className="apd-notes-list">
                          {notes.length === 0
                            ? <div className="apd-empty-msg">No notes yet.</div>
                            : notes.map((n) => (
                              <div key={n.id} className="apd-note-item">
                                <div className="apd-note-meta">
                                  <span>{new Date(n.created_at).toLocaleString()}</span>
                                  {n.created_by && <span>· {n.created_by}</span>}
                                </div>
                                <div className="apd-note-text">{n.note_text}</div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>

                    <div className="apd-save-row">
                      <button className="apd-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : '✓ Save Changes'}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Documents ────────────────────────────────────────── */}
                {activeTab === 'documents' && (
                  <div className="apd-tab-content">
                    {/* Upload */}
                    <div className="apd-upload-box">
                      <h4>Upload Document</h4>
                      <div className="apd-upload-row">
                        <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                          onChange={(e) => setUploadFile(e.target.files[0])} />
                        <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
                          {DOCUMENT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                        <input type="text" placeholder="Description (optional)" value={uploadDescription}
                          onChange={(e) => setUploadDescription(e.target.value)} />
                        <button className="apd-btn-primary" onClick={handleUploadDocument} disabled={!uploadFile || uploading}>
                          {uploading ? 'Uploading…' : 'Upload'}
                        </button>
                      </div>
                    </div>

                    {/* List */}
                    {documents.length === 0 ? (
                      <div className="apd-empty-msg" style={{ padding: '32px', textAlign: 'center' }}>No documents uploaded yet.</div>
                    ) : (
                      <div className="apd-docs-grid">
                        {documents.map((doc) => (
                          <div key={doc.id} className="apd-doc-card">
                            <div className="apd-doc-header">
                              <span className="apd-doc-icon">📄</span>
                              <span className="apd-doc-cat">{doc.category || 'Other'}</span>
                            </div>
                            <div className="apd-doc-name" onClick={() => handleDownloadDocument(doc.id)} title="Download">
                              {doc.original_filename}
                            </div>
                            {doc.description && <div className="apd-doc-desc">{doc.description}</div>}
                            <div className="apd-doc-meta">{formatFileSize(doc.file_size)} · {new Date(doc.upload_date).toLocaleDateString()}</div>
                            <div className="apd-doc-actions">
                              <button className="apd-doc-btn" onClick={() => handleDownloadDocument(doc.id)}>Download</button>
                              <button className="apd-doc-btn delete" onClick={() => handleDeleteDocument(doc.id)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── References ───────────────────────────────────────── */}
                {activeTab === 'references' && (
                  <div className="apd-tab-content apd-empty-msg" style={{ padding: '40px', textAlign: 'center' }}>
                    References section coming soon.
                  </div>
                )}

                {/* ── Milestones ───────────────────────────────────────── */}
                {activeTab === 'milestones' && (
                  <div className="apd-tab-content">
                    <ApplicationMilestones applicant={formData} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Approval Modal ───────────────────────────────────── */}
          {showApprovalModal && (
            <div className="apd-modal-overlay" onClick={() => setShowApprovalModal(false)}>
              <div className="apd-modal" onClick={(e) => e.stopPropagation()}>
                <div className="apd-modal-header">
                  <h2>Approve Applicant for Lease</h2>
                  <button className="apd-modal-close" onClick={() => setShowApprovalModal(false)}>×</button>
                </div>
                <div className="apd-modal-body">
                  <p>Approving <strong>{applicantName}</strong> will prepare them for lease signing.</p>
                  
                  <div className="apd-field">
                    <label>Property *</label>
                    <select
                      className="apd-input"
                      value={approvalData.property_id}
                      onChange={(e) => {
                        setApprovalData(prev => ({ ...prev, property_id: e.target.value, unit_id: '' }));
                        fetchUnits(e.target.value);
                      }}
                    >
                      <option value="">Select property…</option>
                      {properties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="apd-field">
                    <label>Unit (Optional)</label>
                    <select
                      className="apd-input"
                      value={approvalData.unit_id}
                      onChange={(e) => setApprovalData(prev => ({ ...prev, unit_id: e.target.value }))}
                      disabled={!approvalData.property_id}
                    >
                      <option value="">Select unit…</option>
                      {units.map(u => (
                        <option key={u.id} value={u.id}>
                          Unit {u.unit_number} - {u.bedrooms}BR/{u.bathrooms}BA - ${u.monthly_rent}/mo
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="apd-field">
                    <label>Monthly Rent ($) *</label>
                    <input
                      type="number"
                      className="apd-input"
                      value={approvalData.monthly_rent}
                      onChange={(e) => setApprovalData(prev => ({ ...prev, monthly_rent: e.target.value }))}
                      placeholder="e.g. 1500"
                    />
                  </div>
                  
                  <div className="apd-field">
                    <label>Move-in Date</label>
                    <input
                      type="date"
                      className="apd-input"
                      value={approvalData.move_in_date || formData?.move_in_date || ''}
                      onChange={(e) => setApprovalData(prev => ({ ...prev, move_in_date: e.target.value }))}
                    />
                  </div>
                  
                  <div className="apd-field">
                    <label>Lease Term</label>
                    <select
                      className="apd-input"
                      value={approvalData.lease_type}
                      onChange={(e) => setApprovalData(prev => ({ ...prev, lease_type: e.target.value }))}
                    >
                      <option value="6 months">6 months</option>
                      <option value="12 months">12 months</option>
                      <option value="18 months">18 months</option>
                      <option value="24 months">24 months</option>
                      <option value="Month-to-month">Month-to-month</option>
                    </select>
                  </div>
                </div>
                <div className="apd-modal-footer">
                  <button className="apd-btn-secondary" onClick={() => setShowApprovalModal(false)}>Cancel</button>
                  <button 
                    className="apd-btn-primary" 
                    onClick={handleApproveForLease}
                    disabled={!approvalData.property_id || !approvalData.monthly_rent || saving}
                  >
                    {saving ? 'Approving…' : '✓ Approve & Send Lease'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Conversion Modal ───────────────────────────────────── */}
          {showConversionModal && (
            <div className="apd-modal-overlay" onClick={() => setShowConversionModal(false)}>
              <div className="apd-modal" onClick={(e) => e.stopPropagation()}>
                <div className="apd-modal-header">
                  <h2>Convert to Tenant</h2>
                  <button className="apd-modal-close" onClick={() => setShowConversionModal(false)}>×</button>
                </div>
                <div className="apd-modal-body">
                  <div className="apd-conversion-summary">
                    <h3>Conversion Summary</h3>
                    <p>Converting <strong>{applicantName}</strong> to a tenant will:</p>
                    <ul>
                      <li>Create a new tenant account with login credentials</li>
                      <li>Transfer all documents to the tenant record</li>
                      <li>Generate rent payment schedule for the lease term</li>
                      <li>Send welcome email with portal access</li>
                      <li>Mark the unit as occupied (if assigned)</li>
                    </ul>
                  </div>
                  
                  <div className="apd-conversion-details">
                    <h4>Lease Details</h4>
                    <div className="apd-detail-row">
                      <span>Property:</span>
                      <strong>{properties.find(p => p.id === (formData?.assigned_property_id || approvalData.property_id))?.name || 'Not assigned'}</strong>
                    </div>
                    <div className="apd-detail-row">
                      <span>Monthly Rent:</span>
                      <strong>${formData?.approved_rent || approvalData.monthly_rent || '—'}</strong>
                    </div>
                    <div className="apd-detail-row">
                      <span>Move-in Date:</span>
                      <strong>{formData?.move_in_date || approvalData.move_in_date || '—'}</strong>
                    </div>
                    <div className="apd-detail-row">
                      <span>Lease Term:</span>
                      <strong>{formData?.lease_length || approvalData.lease_type || '12 months'}</strong>
                    </div>
                  </div>
                  
                  {/* If not pre-approved, show fields to fill */}
                  {!formData?.assigned_property_id && (
                    <>
                      <hr style={{ margin: '16px 0' }} />
                      <p style={{ color: '#f59e0b' }}>Missing lease details. Please fill in:</p>
                      
                      <div className="apd-field">
                        <label>Property *</label>
                        <select
                          className="apd-input"
                          value={approvalData.property_id}
                          onChange={(e) => {
                            setApprovalData(prev => ({ ...prev, property_id: e.target.value, unit_id: '' }));
                            fetchUnits(e.target.value);
                          }}
                        >
                          <option value="">Select property…</option>
                          {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="apd-field">
                        <label>Unit</label>
                        <select
                          className="apd-input"
                          value={approvalData.unit_id}
                          onChange={(e) => setApprovalData(prev => ({ ...prev, unit_id: e.target.value }))}
                          disabled={!approvalData.property_id}
                        >
                          <option value="">Select unit…</option>
                          {units.map(u => (
                            <option key={u.id} value={u.id}>Unit {u.unit_number}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="apd-field">
                        <label>Monthly Rent ($) *</label>
                        <input
                          type="number"
                          className="apd-input"
                          value={approvalData.monthly_rent}
                          onChange={(e) => setApprovalData(prev => ({ ...prev, monthly_rent: e.target.value }))}
                        />
                      </div>
                      
                      <div className="apd-field">
                        <label>Move-in Date *</label>
                        <input
                          type="date"
                          className="apd-input"
                          value={approvalData.move_in_date}
                          onChange={(e) => setApprovalData(prev => ({ ...prev, move_in_date: e.target.value }))}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="apd-modal-footer">
                  <button className="apd-btn-secondary" onClick={() => setShowConversionModal(false)}>Cancel</button>
                  <button 
                    className="apd-btn-primary" 
                    onClick={handleConvertToTenant}
                    disabled={converting}
                    style={{ backgroundColor: '#7c3aed' }}
                  >
                    {converting ? 'Converting…' : '🔄 Convert to Tenant'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
